/**
 * WeaveIn.ai Platform - Custom Fields Hook
 * React hook for managing organization custom field definitions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  membersAPI,
  CustomField,
  CustomFieldCreate,
  FieldMapping,
  FieldMappingCreate,
  FieldCategory,
  FieldCategoryCreate,
} from '@/services/api/members';
import { parseApiError, ApiCache } from '@/utils/apiErrors';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import useRefreshOnFocus from '@/hooks/useRefreshOnFocus';

/** Extract a string error message from an API error, handling FastAPI 422 array format */
function extractErrorMessage(err: any, fallback: string): string {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e.msg || String(e)).join('; ');
  }
  return detail || err.message || fallback;
}

interface UseCustomFieldsOptions {
  category?: string;
  include_hidden?: boolean;
  /** Filter to fields applicable to this record type. 'activity_event' is the
   *  Somos / FUERTES anonymous-outreach scope; member/contact share the
   *  members table and are scoped via applies_to_record_types. */
  record_type?: 'member' | 'contact' | 'activity_event' | 'family_member' | 'household' | 'caregiver';
  /** Include fields that belong to a repeatable field group. Defaults to false:
   *  a group child is a column of a group instance, not a standalone field, so
   *  rendering one on an entry form shows a field the user can't meaningfully
   *  fill — and a MANDATORY child then blocks the save outright (TRUS-302).
   *  Only the field-group / field-config admin screens should set this. */
  include_group_children?: boolean;
  autoFetch?: boolean;
}

interface UseCustomFieldsReturn {
  fields: CustomField[];
  categories: string[];
  fieldCategories: FieldCategory[];
  mappings: FieldMapping[];
  loading: boolean;
  error: string | null;
  errorAction?: string;
  retryable: boolean;
  /** True when the last attempt to load the field definitions FAILED, as opposed
   *  to succeeding and returning nothing. Consumers must branch on this before
   *  telling the user a field has no options configured: when this endpoint 500s
   *  the caller still has a field list (it comes from the profile-fields
   *  endpoint) but no field_options, and "No options are configured for this
   *  field" is then a lie that reads as a configuration bug (TRUS-501/502/503). */
  fieldsUnavailable: boolean;
  createField: (data: CustomFieldCreate) => Promise<CustomField | null>;
  updateField: (id: number, data: Partial<Omit<CustomFieldCreate, 'name'>>) => Promise<CustomField | null>;
  deleteField: (id: number) => Promise<boolean>;
  bulkCreateFields: (fields: CustomFieldCreate[]) => Promise<CustomField[]>;
  createMapping: (data: FieldMappingCreate) => Promise<FieldMapping | null>;
  deleteMapping: (id: number) => Promise<boolean>;
  fetchMappings: (dbColumn?: string) => Promise<void>;
  createFieldCategory: (data: FieldCategoryCreate) => Promise<FieldCategory | null>;
  refetch: () => Promise<void>;
}

// Cache for custom fields (1 minute success, 10 seconds error)
/** Attempts (including the first) for a RETRYABLE field-definition load, and the
 *  base backoff between them. A 5xx here blanks every select on the member
 *  profile, so it is worth a few hundred milliseconds to ride out a restart or a
 *  deploy rather than leave the user looking at empty dropdowns until they
 *  reload the page. Non-retryable failures (401/403/404) are not retried. */
const FIELDS_MAX_ATTEMPTS = 3;
const FIELDS_RETRY_BASE_MS = 400;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fieldsCache = new ApiCache<CustomField[]>(60000, 10000);
const categoriesCache = new ApiCache<string[]>(60000, 10000);
const fieldCategoriesCache = new ApiCache<FieldCategory[]>(60000, 10000);

/** Cache key for a custom-field list. MUST include the active org: these
 *  caches are module-level and survive an org switch, so an org-agnostic key
 *  served the previous org's fields to the next one for up to the 60s TTL.
 *  In prod that put another org's field ids on the Field Config screen, and
 *  saving from there wrote config rows against foreign com_fields. */
function fieldsCacheKey(
  orgId: string,
  category: string | undefined,
  includeHidden: boolean,
  recordType: string | undefined,
  includeGroupChildren: boolean
): string {
  return `fields:${orgId}:${category || 'all'}:${includeHidden}:${recordType || 'all'}:${includeGroupChildren}`;
}

export default function useCustomFields(
  options: UseCustomFieldsOptions = {}
): UseCustomFieldsReturn {
  const {
    category,
    include_hidden = false,
    record_type,
    include_group_children = false,
    autoFetch = true,
  } = options;

  // Re-runs every fetch below when the user switches org (client-side switch,
  // no reload) and scopes the module-level caches so one org can never be
  // served another org's field definitions.
  const orgId = useActiveOrgId();

  const [fields, setFields] = useState<CustomField[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [fieldCategories, setFieldCategories] = useState<FieldCategory[]>([]);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<string | undefined>(undefined);
  const [retryable, setRetryable] = useState(false);
  const [fieldsUnavailable, setFieldsUnavailable] = useState(false);

  // Track if component is mounted to avoid state updates after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchFields = useCallback(async () => {
    const cacheKey = fieldsCacheKey(orgId, category, include_hidden, record_type, include_group_children);

    // Check cache first
    const cached = fieldsCache.get(cacheKey);
    if (cached && !cached.isError) {
      setFields(cached.data);
      return;
    }

    if (!mountedRef.current) return;
    setLoading(true);
    setError(null);
    setErrorAction(undefined);
    setRetryable(false);

    try {
      let lastErr: any;

      for (let attempt = 1; attempt <= FIELDS_MAX_ATTEMPTS; attempt++) {
        try {
          const fieldsData = await fieldsCache.dedupedRequest(cacheKey, () =>
            membersAPI.listCustomFields({
              category,
              include_hidden,
              record_type,
              include_group_children,
            })
          );

          if (!mountedRef.current) return;
          setFields(fieldsData);
          setFieldsUnavailable(false);
          fieldsCache.set(cacheKey, fieldsData);
          return;
        } catch (err: any) {
          lastErr = err;

          // Give up immediately on anything a retry cannot fix (401/403/404) and
          // on the last attempt. Only transient faults — 5xx, timeouts, a dropped
          // connection — are worth going round again for.
          if (!parseApiError(err).retryable || attempt === FIELDS_MAX_ATTEMPTS) break;
          if (!mountedRef.current) return;

          // Exponential backoff with jitter, so a restarting API does not take a
          // synchronised thundering herd from every open tab.
          const backoff = FIELDS_RETRY_BASE_MS * 2 ** (attempt - 1);
          await sleep(backoff + Math.random() * FIELDS_RETRY_BASE_MS);
        }
      }

      throw lastErr;
    } catch (err: any) {
      if (!mountedRef.current) return;

      const errorInfo = parseApiError(err);
      setError(errorInfo.message);
      setErrorAction(errorInfo.action);
      setRetryable(errorInfo.retryable);
      // Leave `fields` untouched rather than blanking it: stale definitions still
      // render the right options, and this flag is what tells the UI to stop
      // claiming a field simply has none configured.
      setFieldsUnavailable(true);

      // Cache the error to prevent spam
      fieldsCache.set(cacheKey, [], true);

      // Only log to console once, not repeatedly
      if (!cached?.isError) {
        console.warn('📋 useCustomFields - Error fetching custom fields:', {
          status: errorInfo.status,
          message: errorInfo.technical,
          retryable: errorInfo.retryable,
        });
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [orgId, category, include_hidden, record_type, include_group_children]);

  const fetchCategories = useCallback(async () => {
    const cacheKey = `categories:${orgId}:all`;

    // Check cache first
    const cached = categoriesCache.get(cacheKey);
    if (cached && !cached.isError) {
      setCategories(cached.data);
      return;
    }

    try {
      const categoriesData = await categoriesCache.dedupedRequest(cacheKey, () =>
        membersAPI.listFieldCategories()
      );

      if (!mountedRef.current) return;
      setCategories(categoriesData);
      categoriesCache.set(cacheKey, categoriesData);
    } catch (err: any) {
      // Silently cache the error - categories are non-critical
      categoriesCache.set(cacheKey, [], true);

      if (!cached?.isError) {
        console.warn('📋 useCustomFields - Error fetching categories (non-critical)');
      }
    }
  }, [orgId]);

  // Fetch FieldCategory entries from the database (structured categories)
  const fetchFieldCategories = useCallback(async () => {
    const cacheKey = `fieldCategories:${orgId}:all`;

    // Check cache first
    const cached = fieldCategoriesCache.get(cacheKey);
    if (cached && !cached.isError) {
      setFieldCategories(cached.data);
      return;
    }

    try {
      const fieldCategoriesData = await fieldCategoriesCache.dedupedRequest(cacheKey, () =>
        membersAPI.listMemberFieldCategories(true)
      );

      if (!mountedRef.current) return;
      setFieldCategories(fieldCategoriesData);
      fieldCategoriesCache.set(cacheKey, fieldCategoriesData);
    } catch (err: any) {
      // Silently cache the error - categories are non-critical
      fieldCategoriesCache.set(cacheKey, [], true);

      if (!cached?.isError) {
        console.warn('📋 useCustomFields - Error fetching field categories (non-critical)');
      }
    }
  }, [orgId]);

  useEffect(() => {
    if (autoFetch) {
      fetchFields();
      fetchCategories();
      fetchFieldCategories();
    }
  }, [autoFetch, fetchFields, fetchCategories, fetchFieldCategories]);

  // A tab left open across a meeting or overnight holds whatever definitions it
  // fetched on mount. Re-check when the user comes back to it, or when the
  // connection returns, so an admin's field change — or a fix deployed in the
  // meantime — lands without anyone reloading the browser. The 60s success TTL
  // on fieldsCache means this is usually a cache hit and costs nothing.
  useRefreshOnFocus(() => {
    if (autoFetch) fetchFields();
  }, { minIntervalMs: 60_000 });

  const createField = async (data: CustomFieldCreate): Promise<CustomField | null> => {
    setLoading(true);
    setError(null);

    try {
      const newField = await membersAPI.createCustomField(data);

      // Update local state
      setFields((prev) => [...prev, newField]);

      // Sync module-level cache so remounts within the TTL window include the new field (TRUS-160).
      const cacheKey = fieldsCacheKey(orgId, category, include_hidden, record_type, include_group_children);
      const cached = fieldsCache.get(cacheKey);
      if (cached && !cached.isError) {
        fieldsCache.set(cacheKey, [...cached.data, newField]);
      }

      // Update categories if new category
      if (newField.category && !categories.includes(newField.category)) {
        setCategories((prev) => [...prev, newField.category!].sort());
      }

      return newField;
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to create custom field'));
      console.error('Error creating custom field:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateField = async (
    id: number,
    // Type is editable as of TRUS-59 #1; only the internal name is immutable.
    data: Partial<Omit<CustomFieldCreate, 'name'>>
  ): Promise<CustomField | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedField = await membersAPI.updateCustomField(id, data);

      // Update in local state
      setFields((prev) =>
        prev.map((field) => (field.id === id ? updatedField : field))
      );

      // Sync module-level cache so remounts within the TTL window serve the
      // updated field instead of restoring stale data (TRUS-160).
      const cacheKey = fieldsCacheKey(orgId, category, include_hidden, record_type, include_group_children);
      const cached = fieldsCache.get(cacheKey);
      if (cached && !cached.isError) {
        fieldsCache.set(cacheKey, cached.data.map((f) => (f.id === id ? updatedField : f)));
      }

      return updatedField;
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to update custom field');
      setError(errorMessage);
      console.error('Error updating custom field:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteField = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await membersAPI.deleteCustomField(id);

      // Remove from local state
      setFields((prev) => prev.filter((field) => field.id !== id));

      // Sync module-level cache so remounts within the TTL window exclude the deleted field (TRUS-160).
      const cacheKey = fieldsCacheKey(orgId, category, include_hidden, record_type, include_group_children);
      const cached = fieldsCache.get(cacheKey);
      if (cached && !cached.isError) {
        fieldsCache.set(cacheKey, cached.data.filter((f) => f.id !== id));
      }

      return true;
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to delete custom field');
      setError(errorMessage);
      console.error('Error deleting custom field:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const bulkCreateFields = async (fieldsData: CustomFieldCreate[]): Promise<CustomField[]> => {
    setLoading(true);
    setError(null);

    try {
      const createdFields = await membersAPI.bulkCreateFields(fieldsData);

      // Update local state
      setFields((prev) => [...prev, ...createdFields]);

      // Update categories
      const newCategories = createdFields
        .map((f) => f.category)
        .filter((c): c is string => !!c && !categories.includes(c));

      if (newCategories.length > 0) {
        setCategories((prev) => [...prev, ...newCategories].sort());
      }

      return createdFields;
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to bulk create fields');
      setError(errorMessage);
      console.error('Error bulk creating fields:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const createMapping = async (data: FieldMappingCreate): Promise<FieldMapping | null> => {
    setLoading(true);
    setError(null);

    try {
      const newMapping = await membersAPI.createFieldMapping(data);

      // Update local state
      setMappings((prev) => [...prev, newMapping]);

      return newMapping;
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to create field mapping');
      setError(errorMessage);
      console.error('Error creating field mapping:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteMapping = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await membersAPI.deleteFieldMapping(id);

      // Remove from local state
      setMappings((prev) => prev.filter((mapping) => mapping.id !== id));

      return true;
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to delete field mapping');
      setError(errorMessage);
      console.error('Error deleting field mapping:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async (dbColumn?: string) => {
    setLoading(true);
    setError(null);

    try {
      const mappingsData = await membersAPI.listFieldMappings(dbColumn);
      setMappings(mappingsData);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to fetch field mappings');
      setError(errorMessage);
      console.error('Error fetching field mappings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create a new FieldCategory entry
  const createFieldCategory = async (data: FieldCategoryCreate): Promise<FieldCategory | null> => {
    setLoading(true);
    setError(null);

    try {
      const newCategory = await membersAPI.createFieldCategory(data);

      // Update local state
      setFieldCategories((prev) => [...prev, newCategory].sort((a, b) => a.sort_order - b.sort_order));

      // Invalidate cache
      fieldCategoriesCache.clear();

      return newCategory;
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to create field category');
      setError(errorMessage);
      console.error('Error creating field category:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchFields();
    await fetchCategories();
    await fetchFieldCategories();
  };

  return {
    fields,
    categories,
    fieldCategories,
    mappings,
    loading,
    error,
    errorAction,
    retryable,
    fieldsUnavailable,
    createField,
    updateField,
    deleteField,
    bulkCreateFields,
    createMapping,
    deleteMapping,
    fetchMappings,
    createFieldCategory,
    refetch,
  };
}
